import { PurchaseOrdersComponent } from './components/purchase-orders/purchase-orders.component'
import { Routes } from '@angular/router'
import { productAccessGuard } from './guards/product-access.guard'
import { MarketplaceComponent } from './components/marketplace/marketplace.component'
import { MarketplaceDetailComponent } from './components/marketplace/marketplace-detail.component'
import { RequisitionCartComponent } from './components/requisition-cart/requisition-cart.component'
import {
  RequisitionsComponent,
  requisitionUnsavedChangesGuard,
} from './components/requisitions/requisitions.component'
import { HomeComponent } from './components/home/home.component'
import { AdvancedSearchComponent } from './components/advanced-search/advanced-search.component'
import { ProductComponent } from './components/product/product.component'
import { ROUTES } from './constants/routes'
import { ProductOverviewComponent } from './components/product/product-overview/product-overview.component'
import { ProductSupplyChainComponent } from './components/product/product-supply-chain/product-supply-chain.component'
import { LoginComponent } from './components/auth/login/login.component'
import { ProductSustainabilityComponent } from './components/product/product-sustainability/product-sustainability.component'
import { ProductChemistryComponent } from './components/product/product-chemistry/product-chemistry.component'
import { ProductScoresComponent } from './components/product/product-scores/product-scores.component'
import { ProductRegulatorInfoComponent } from './components/product/product-regulator-info/product-regulator-info.component'
import { AdminComponent } from './components/admin/admin.component'
import { UsersComponent } from './components/admin/users/users.component'
import { UserCreateComponent } from './components/admin/users/user-create/user-create.component'
import { SetPasswordComponent } from './components/auth/set-password/set-password.component'
import { AuthComponent } from './components/auth/auth.component'
import {
  adminGuard,
  authRedirectGuard,
  loggedInGuard,
  masterGuard,
  requisitionCartGuard,
} from './guards/auth.guard'
import { ResetPasswordComponent } from './components/auth/reset-password/reset-password.component'
import { CompanyProductsComponent } from './components/companies/company-products/company-products.component'
import { CreateCompanyComponent } from './components/companies/create-company/create-company.component'
import { IntegrationsComponent } from './components/admin/integrations/integrations.component'
import { CompanyComponent } from './components/companies/company.component'
import { SubstancesComponent } from './components/substances/substances.component'
import { LiabilityDisclaimerComponent } from './components/liability-disclaimer/liability-disclaimer.component'
import { ProductIngredientsComponent } from './components/product/product-ingredients/product-ingredients.component'
import { IngredientsComponent } from './components/ingredients/ingredients.component'
import { IngredientCreateComponent } from './components/ingredients/ingredient-create/ingredient-create.component'
import { SettingsComponent } from './components/settings/settings.component'
import { LanguageComponent } from './components/settings/language/language.component'
import { CreateProductTemplateComponent } from './components/settings/product-csv-templates/create-product-template/create-product-template.component'
import { ProductAddComponent } from './components/product-add/product-add.component'
import { ImportProductsComponent } from './components/companies/company-products/import-products/import-products.component'
import { CreateIngredientTemplateComponent } from './components/settings/ingredient-csv-templates/create-ingredient-template/create-ingredient-template.component'
import { ImportIngredientsComponent } from './components/ingredients/import-ingredients/import-ingredients.component'
import { ProductPlasticsDeclarationComponent } from './components/product/product-plastics-declaration/product-plastics-declaration.component'
import { ReportsComponent } from './components/admin/reports/reports.component'
import { AddToCatalogueComponent } from './components/companies/company-products/add-to-catalogue/add-to-catalogue.component'
import { FprReportComponent } from './components/admin/reports/fpr-report/fpr-report.component'
import { CreateFprTemplateComponent } from './components/settings/fpr-templates/create-fpr-template/create-fpr-template.component'
import { CreateProductSharingTemplateComponent } from './components/settings/product-sharing-templates/create-product-sharing-template/create-product-sharing-template.component'
import { BulkShareComponent } from './components/companies/company-products/bulk-share/bulk-share.component'

export const routes: Routes = [
  {
    ...ROUTES.PURCHASE_ORDERS,
    component: PurchaseOrdersComponent,
    canActivate: [requisitionCartGuard],
  },
  {
    path: 'purchase-orders/:id',
    title: ROUTES.PURCHASE_ORDERS.title,
    component: PurchaseOrdersComponent,
    canActivate: [requisitionCartGuard],
  },
  {
    ...ROUTES.MARKETPLACE,
    component: MarketplaceComponent,
    canActivate: [loggedInGuard],
  },
  {
    path: 'marketplace/:id',
    title: ROUTES.MARKETPLACE.title,
    component: MarketplaceDetailComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.REQUISITION_CART,
    component: RequisitionCartComponent,
    canActivate: [requisitionCartGuard],
  },
  {
    ...ROUTES.REQUISITIONS,
    component: RequisitionsComponent,
    canActivate: [requisitionCartGuard],
  },
  {
    path: 'requisitions/:id',
    title: ROUTES.REQUISITIONS.title,
    component: RequisitionsComponent,
    canActivate: [requisitionCartGuard],
    canDeactivate: [requisitionUnsavedChangesGuard],
  },
  {
    path: '',
    title: ROUTES.VITE_MARKETPLACE.title,
    component: HomeComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.ADVANCED_SEARCH,
    component: AdvancedSearchComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.PRODUCT,
    component: ProductComponent,
    canActivate: [loggedInGuard],
    canActivateChild: [productAccessGuard],
    children: [
      {
        ...ROUTES.PRODUCT_OVERVIEW,
        path: '',
        component: ProductOverviewComponent,
      },
      {
        ...ROUTES.PRODUCT_OVERVIEW,
        component: ProductOverviewComponent,
      },
      {
        ...ROUTES.PRODUCT_SUPPLY_CHAIN,
        component: ProductSupplyChainComponent,
      },
      {
        ...ROUTES.PRODUCT_SUSTAINABILITY,
        component: ProductSustainabilityComponent,
      },
      {
        ...ROUTES.PRODUCT_COMPONENTS,
        component: ProductChemistryComponent,
      },
      {
        ...ROUTES.PRODUCT_SCORES,
        component: ProductScoresComponent,
      },
      {
        ...ROUTES.PRODUCT_REGULATIONS,
        component: ProductRegulatorInfoComponent,
      },
      {
        ...ROUTES.PRODUCT_INGREDIENTS,
        component: ProductIngredientsComponent,
      },
      {
        ...ROUTES.PRODUCT_PLASTICS_DECLARATION,
        component: ProductPlasticsDeclarationComponent,
      },
    ],
  },
  {
    ...ROUTES.ADMIN,
    component: AdminComponent,
    canActivate: [adminGuard],
    children: [
      {
        title: ROUTES.USERS.title,
        path: '',
        component: UsersComponent,
      },
      {
        ...ROUTES.USERS,
        component: UsersComponent,
      },
      {
        ...ROUTES.USERS_CREATE,
        component: UserCreateComponent,
      },
      {
        ...ROUTES.COMPANY,
        children: [
          {
            canActivate: [masterGuard],
            path: '',
            component: CompanyComponent,
          },
          {
            canActivate: [masterGuard],
            ...ROUTES.CREATE_COMPANY,
            component: CreateCompanyComponent,
          },
          {
            ...ROUTES.COMPANY_PRODUCTS,
            component: CompanyProductsComponent,
          },
        ],
      },
      {
        ...ROUTES.INTEGRATIONS,
        component: IntegrationsComponent,
      },
      {
        ...ROUTES.REPORTS,
        component: ReportsComponent,
        canActivate: [adminGuard],
      },
      {
        ...ROUTES.ADD_FPR_REPORT,
        component: FprReportComponent,
      },
      {
        ...ROUTES.FPR_REPORT_EDIT,
        component: FprReportComponent,
      },
    ],
  },
  {
    ...ROUTES.AUTH,
    component: AuthComponent,
    canActivate: [authRedirectGuard],
    children: [
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      {
        ...ROUTES.LOGIN,
        component: LoginComponent,
      },
      {
        ...ROUTES.SET_PASSWORD,
        component: SetPasswordComponent,
      },
      {
        ...ROUTES.RESET_PASSWORD,
        component: ResetPasswordComponent,
      },
    ],
  },
  {
    ...ROUTES.PRODUCT_ADD,
    component: ProductAddComponent,
    canActivate: [adminGuard],
  },
  {
    ...ROUTES.SUBSTANCES,
    component: SubstancesComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.LIABILITY_DISCLAIMER,
    component: LiabilityDisclaimerComponent,
  },
  {
    ...ROUTES.INGREDIENTS,
    component: IngredientsComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.INGREDIENTS_CREATE,
    component: IngredientCreateComponent,
    canActivate: [adminGuard],
  },
  {
    ...ROUTES.INGREDIENTS_EDIT,
    component: IngredientCreateComponent,
    canActivate: [adminGuard],
  },
  {
    ...ROUTES.SETTINGS,
    component: SettingsComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.LANGUAGE,
    component: LanguageComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.PRODUCT_CSV_TEMPLATES,
    component: CreateProductTemplateComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.PRODUCT_CSV_TEMPLATES_EDIT,
    component: CreateProductTemplateComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.IMPORT_PRODUCTS,
    component: ImportProductsComponent,
    canActivate: [adminGuard],
  },
  {
    ...ROUTES.INGREDIENT_CSV_TEMPLATES,
    component: CreateIngredientTemplateComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.INGREDIENT_CSV_TEMPLATES_EDIT,
    component: CreateIngredientTemplateComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.IMPORT_INGREDIENTS,
    component: ImportIngredientsComponent,
    canActivate: [adminGuard],
  },
  {
    ...ROUTES.ADD_TO_CATALOGUE,
    component: AddToCatalogueComponent,
  },
  {
    ...ROUTES.FPR_TEMPLATES,
    component: CreateFprTemplateComponent,
    canActivate: [adminGuard],
  },
  {
    ...ROUTES.FPR_TEMPLATE_EDIT,
    component: CreateFprTemplateComponent,
    canActivate: [adminGuard],
  },
  {
    ...ROUTES.PRODUCT_SHARING_CSV_TEMPLATES,
    component: CreateProductSharingTemplateComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.PRODUCT_SHARING_CSV_TEMPLATES_EDIT,
    component: CreateProductSharingTemplateComponent,
    canActivate: [loggedInGuard],
  },
  {
    ...ROUTES.IMPORT_PRODUCT_SHARING,
    component: BulkShareComponent,
    canActivate: [adminGuard],
  },
  { path: '**', redirectTo: '' },
]
